import React from 'react';
import { 
  Landmark, Globe, Compass, ArrowRight, ShieldCheck, Scale,
  FileSignature, ScrollText, ClipboardList, Info, ChevronRight,
  Briefcase, Coins, Truck, Users, HardHat, HeartPulse, TreePine
} from 'lucide-react';
import { LegalDocument, LegalOrigin, DocumentNature, LegalDomain } from '../../types';
import { StatusBadge } from '../ui/StatusBadge';
import { formatNature } from '../../utils/legalUtils';
import { useAuth } from '../../Hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useAuthModal } from '../../contexts/AuthModalContext';

interface CategoryViewProps {
  documents: LegalDocument[];
  setView: (view: string) => void;
  setActiveDocumentId: (id: number | null) => void;
  setSearchQueryState?: (q: string) => void;
  setSearchFiltersState: (filters: any) => void;
  selectedOrigin: LegalOrigin | null;
  setSelectedOrigin: (origin: LegalOrigin | null) => void;
}

export const CategoryView: React.FC<CategoryViewProps> = ({
  documents,
  setView,
  setActiveDocumentId,
  setSearchQueryState,
  setSearchFiltersState,
  selectedOrigin,
  setSelectedOrigin
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openAuthModal } = useAuthModal();
  const [activeTab, setActiveTab] = React.useState<LegalOrigin>(selectedOrigin || 'NATIONAL');
  const [selectedNature, setSelectedNature] = React.useState<DocumentNature | null>(null);
  const [selectedDomain, setSelectedDomain] = React.useState<LegalDomain | null>(null);

  // Sync state upward when tab changes
  const handleTabChange = (origin: LegalOrigin) => {
    setActiveTab(origin);
    setSelectedOrigin(origin);
    setSelectedNature(null);
    setSelectedDomain(null);
  };

  // Categories under National
  const nationalCategories = [
    { 
      name: 'Constitutions', nature: 'CONSTITUTION' as DocumentNature, 
      count: documents.filter(d => d.nature === 'CONSTITUTION').length, 
      desc: 'Sommet des normes, définit la structure étatique',
      icon: <Landmark className="w-5 h-5 text-amber-600" />,
      theme: { bg: 'bg-gradient-to-br from-amber-50/80 to-white', border: 'border-amber-200/60', text: 'text-amber-950', badge: 'bg-amber-100 text-amber-700 border-amber-200/50', btn: 'text-amber-700 hover:text-amber-900', ring: 'ring-amber-500/20' }
    },
    { 
      name: 'Lois', nature: 'LOI' as DocumentNature, 
      count: documents.filter(d => d.nature === 'LOI').length, 
      desc: 'Textes votés par le pouvoir législatif (Sénat et Assemblée)',
      icon: <Scale className="w-5 h-5 text-blue-600" />,
      theme: { bg: 'bg-gradient-to-br from-blue-50/80 to-white', border: 'border-blue-200/60', text: 'text-blue-950', badge: 'bg-blue-100 text-blue-700 border-blue-200/50', btn: 'text-blue-700 hover:text-blue-900', ring: 'ring-blue-500/20' }
    },
    { 
      name: 'Ordonnances', nature: 'ORDONNANCE' as DocumentNature, 
      count: documents.filter(d => d.nature === 'ORDONNANCE').length, 
      desc: 'Mesures prises par le Président sur habilitation législative',
      icon: <FileSignature className="w-5 h-5 text-fuchsia-600" />,
      theme: { bg: 'bg-gradient-to-br from-fuchsia-50/80 to-white', border: 'border-fuchsia-200/60', text: 'text-fuchsia-950', badge: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200/50', btn: 'text-fuchsia-700 hover:text-fuchsia-900', ring: 'ring-fuchsia-500/20' }
    },
    { 
      name: 'Décrets', nature: 'DECRET' as DocumentNature, 
      count: documents.filter(d => d.nature === 'DECRET').length, 
      desc: 'Actes règlementaires présidentiels ou premier ministériels',
      icon: <ScrollText className="w-5 h-5 text-emerald-600" />,
      theme: { bg: 'bg-gradient-to-br from-emerald-50/80 to-white', border: 'border-emerald-200/60', text: 'text-emerald-950', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200/50', btn: 'text-emerald-700 hover:text-emerald-900', ring: 'ring-emerald-500/20' }
    },
    { 
      name: 'Arrêtés', nature: 'ARRETE' as DocumentNature, 
      count: documents.filter(d => d.nature === 'ARRETE').length, 
      desc: 'Décisions ministérielles, provinciales ou préfectorales',
      icon: <ClipboardList className="w-5 h-5 text-orange-600" />,
      theme: { bg: 'bg-gradient-to-br from-orange-50/80 to-white', border: 'border-orange-200/60', text: 'text-orange-950', badge: 'bg-orange-100 text-orange-700 border-orange-200/50', btn: 'text-orange-700 hover:text-orange-900', ring: 'ring-orange-500/20' }
    },
    { 
      name: 'Circulaires', nature: 'CIRCULAIRE' as DocumentNature, 
      count: documents.filter(d => d.nature === 'CIRCULAIRE').length, 
      desc: 'Instructions internes interprétant les lois existantes',
      icon: <Info className="w-5 h-5 text-sky-600" />,
      theme: { bg: 'bg-gradient-to-br from-sky-50/80 to-white', border: 'border-sky-200/60', text: 'text-sky-950', badge: 'bg-sky-100 text-sky-700 border-sky-200/50', btn: 'text-sky-700 hover:text-sky-900', ring: 'ring-sky-500/20' }
    }
  ];

  // Families under Community
  const communityCategories = [
    { 
      name: 'Commerce & Affaires', domain: 'DROIT_COMMERCIAL' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_COMMERCIAL' && d.legalOrigin === 'COMMUNITY').length, 
      desc: 'Actes uniformes, commerce, douanes (ex: Traité OHADA)',
      icon: <Briefcase className="w-5 h-5 text-indigo-600" />,
      theme: { bg: 'bg-gradient-to-br from-indigo-50/80 to-white', border: 'border-indigo-200/60', text: 'text-indigo-950', badge: 'bg-indigo-100 text-indigo-700 border-indigo-200/50', btn: 'text-indigo-700 hover:text-indigo-900', ring: 'ring-indigo-500/20' }
    },
    { 
      name: 'Droits de l\'Homme', domain: 'DROITS_DE_L_HOMME' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROITS_DE_L_HOMME' && d.legalOrigin === 'COMMUNITY').length, 
      desc: 'Protection des droits fondamentaux (ex: Charte Africaine)',
      icon: <ShieldCheck className="w-5 h-5 text-rose-600" />,
      theme: { bg: 'bg-gradient-to-br from-rose-50/80 to-white', border: 'border-rose-200/60', text: 'text-rose-950', badge: 'bg-rose-100 text-rose-700 border-rose-200/50', btn: 'text-rose-700 hover:text-rose-900', ring: 'ring-rose-500/20' }
    },
    { 
      name: 'Banque & Finance', domain: 'DROIT_BANQUE' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_BANQUE' && d.legalOrigin === 'COMMUNITY').length, 
      desc: 'Réglementations bancaires (ex: COBAC)',
      icon: <Coins className="w-5 h-5 text-yellow-600" />,
      theme: { bg: 'bg-gradient-to-br from-yellow-50/80 to-white', border: 'border-yellow-200/60', text: 'text-yellow-950', badge: 'bg-yellow-100 text-yellow-700 border-yellow-200/50', btn: 'text-yellow-700 hover:text-yellow-900', ring: 'ring-yellow-500/20' }
    },
    { 
      name: 'Droit Douanier', domain: 'DROIT_DOUANIER' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_DOUANIER' && d.legalOrigin === 'COMMUNITY').length, 
      desc: 'Réglementations régionales d\'intégration douanière (ex: CEMAC)',
      icon: <Truck className="w-5 h-5 text-teal-600" />,
      theme: { bg: 'bg-gradient-to-br from-teal-50/80 to-white', border: 'border-teal-200/60', text: 'text-teal-950', badge: 'bg-teal-100 text-teal-700 border-teal-200/50', btn: 'text-teal-700 hover:text-teal-900', ring: 'ring-teal-500/20' }
    }
  ];

  // Categories under International
  const internationalCategories = [
    { 
      name: 'Droit International', domain: 'DROIT_INTERNATIONAL' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_INTERNATIONAL' && d.legalOrigin === 'INTERNATIONAL').length, 
      desc: 'Traités d\'alliance, accords multilatéraux',
      icon: <Globe className="w-5 h-5 text-blue-600" />,
      theme: { bg: 'bg-gradient-to-br from-blue-50/80 to-white', border: 'border-blue-200/60', text: 'text-blue-950', badge: 'bg-blue-100 text-blue-700 border-blue-200/50', btn: 'text-blue-700 hover:text-blue-900', ring: 'ring-blue-500/20' }
    },
    { 
      name: 'Droits de l\'Homme', domain: 'DROITS_DE_L_HOMME' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROITS_DE_L_HOMME' && d.legalOrigin === 'INTERNATIONAL').length, 
      desc: 'Pactes et conventions (ex: Convention des Nations Unies)',
      icon: <Users className="w-5 h-5 text-rose-600" />,
      theme: { bg: 'bg-gradient-to-br from-rose-50/80 to-white', border: 'border-rose-200/60', text: 'text-rose-950', badge: 'bg-rose-100 text-rose-700 border-rose-200/50', btn: 'text-rose-700 hover:text-rose-900', ring: 'ring-rose-500/20' }
    },
    { 
      name: 'Droit du Travail', domain: 'DROIT_TRAVAIL' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_TRAVAIL' && d.legalOrigin === 'INTERNATIONAL').length, 
      desc: 'Conventions de l\'Organisation Internationale du Travail (OIT)',
      icon: <HardHat className="w-5 h-5 text-amber-600" />,
      theme: { bg: 'bg-gradient-to-br from-amber-50/80 to-white', border: 'border-amber-200/60', text: 'text-amber-950', badge: 'bg-amber-100 text-amber-700 border-amber-200/50', btn: 'text-amber-700 hover:text-amber-900', ring: 'ring-amber-500/20' }
    },
    { 
      name: 'Santé', domain: 'DROIT_SANTE' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_SANTE' && d.legalOrigin === 'INTERNATIONAL').length, 
      desc: 'Traités et accords internationaux relatifs à la santé publique',
      icon: <HeartPulse className="w-5 h-5 text-red-500" />,
      theme: { bg: 'bg-gradient-to-br from-red-50/80 to-white', border: 'border-red-200/60', text: 'text-red-950', badge: 'bg-red-100 text-red-700 border-red-200/50', btn: 'text-red-700 hover:text-red-900', ring: 'ring-red-500/20' }
    },
    { 
      name: 'Environnement', domain: 'DROIT_ENVIRONNEMENT' as LegalDomain, 
      count: documents.filter(d => d.areaOfLaw === 'DROIT_ENVIRONNEMENT' && d.legalOrigin === 'INTERNATIONAL').length, 
      desc: 'Accords climatiques et préservation de la nature',
      icon: <TreePine className="w-5 h-5 text-green-600" />,
      theme: { bg: 'bg-gradient-to-br from-green-50/80 to-white', border: 'border-green-200/60', text: 'text-green-950', badge: 'bg-green-100 text-green-700 border-green-200/50', btn: 'text-green-700 hover:text-green-900', ring: 'ring-green-500/20' }
    }
  ];

  // Filter documents that match the current select
  const filteredDocs = React.useMemo(() => {
    let list = documents.filter(d => d.legalOrigin === activeTab);
    if (selectedNature) {
      list = list.filter(d => d.nature === selectedNature);
    }
    if (selectedDomain) {
      list = list.filter(d => d.areaOfLaw === selectedDomain);
    }
    return list;
  }, [documents, activeTab, selectedNature, selectedDomain]);

  const selectNatureAndRedirect = (nature: DocumentNature, _name?: string) => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    setSearchQueryState?.('');
    setSearchFiltersState({ legalOrigin: activeTab, nature: nature as string });
    setView('search');
  };

  const selectDomainAndRedirect = (domain: LegalDomain, _name?: string) => {
    if (!isAuthenticated) {
      openAuthModal();
      return;
    }
    setSearchQueryState?.('');
    setSearchFiltersState({ legalOrigin: activeTab, areaOfLaw: domain as string });
    setView('search');
  };

  const getSelectedLabel = () => {
    if (selectedNature) return formatNature(selectedNature);
    if (selectedDomain) return selectedDomain.replace(/_/g, ' ').replace('DROIT ', 'Droit ');
    return '';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      
      {/* Page header */}
      <div className="space-y-1">
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">
          Explorateur par Catégories Doctrinales
        </h2>
        <p className="text-zinc-500 text-xs sm:text-sm font-normal">
          Naviguez au sein de la base légale structurée selon l'origine de législation et la hiérarchie kelsénienne.
        </p>
      </div>

      {/* THREE LEGISLATION TAB SYSTEM (SECTION 12) */}
      <div className="flex bg-zinc-100/80 backdrop-blur p-1 rounded-full max-w-xl mx-auto border border-zinc-200/50 shadow-sm">
        <button
          onClick={() => handleTabChange('NATIONAL')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 px-2 sm:px-4 text-[9px] sm:text-[11px] font-bold uppercase tracking-wider rounded-full transition-all duration-300 cursor-pointer ${activeTab === 'NATIONAL' ? 'bg-slate-900 text-white shadow-md' : 'text-zinc-650 hover:text-zinc-900 hover:bg-zinc-200/40'}`}
        >
          <Landmark className="w-3.5 h-3.5 opacity-90" /> National
        </button>

        <button
          onClick={() => handleTabChange('COMMUNITY')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 px-2 sm:px-4 text-[9px] sm:text-[11px] font-bold uppercase tracking-wider rounded-full transition-all duration-300 cursor-pointer ${activeTab === 'COMMUNITY' ? 'bg-slate-900 text-white shadow-md' : 'text-zinc-650 hover:text-zinc-900 hover:bg-zinc-200/40'}`}
        >
          <Globe className="w-3.5 h-3.5 opacity-90" /> Communautaire
        </button>

        <button
          onClick={() => handleTabChange('INTERNATIONAL')}
          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-2.5 px-2 sm:px-4 text-[9px] sm:text-[11px] font-bold uppercase tracking-wider rounded-full transition-all duration-300 cursor-pointer ${activeTab === 'INTERNATIONAL' ? 'bg-slate-900 text-white shadow-md' : 'text-zinc-650 hover:text-zinc-900 hover:bg-zinc-200/40'}`}
        >
          <Compass className="w-3.5 h-3.5 opacity-90" /> Internationale
        </button>
      </div>



      {/* SUB-CATEGORY CARDS INDEX GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* NATIONAL RENDER */}
        {activeTab === 'NATIONAL' && nationalCategories.map((cat) => {
          const isSelected = selectedNature === cat.nature;
          return (
            <div
              key={cat.nature}
              onClick={() => setSelectedNature(isSelected ? null : cat.nature)}
              className={`p-6 rounded-3xl border cursor-pointer transition-all duration-300 flex flex-col justify-between group ${cat.theme.bg} ${isSelected ? `ring-4 ring-offset-2 ${cat.theme.ring} ${cat.theme.border} scale-[1.02] shadow-lg` : `${cat.theme.border} hover:shadow-lg hover:-translate-y-1`}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-2xl bg-white shadow-sm border border-white/40 ring-1 ring-primary-750/5`}>
                    {cat.icon}
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <span className={`text-sm font-extrabold uppercase tracking-wide ${cat.theme.text}`}>
                    {cat.name}
                  </span>
                  <p className="text-xs text-zinc-600 leading-relaxed font-medium">{cat.desc}</p>
                </div>
              </div>

              <div className={`pt-5 flex items-center justify-between text-[11px] font-bold border-t border-primary-750/10 mt-5`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectNatureAndRedirect(cat.nature);
                  }}
                  className={`${cat.theme.btn} flex items-center gap-1.5 focus:outline-none transition-colors group-hover:underline decoration-2 underline-offset-4`}
                >
                  Directement dans le moteur <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <span className={`${cat.theme.text} uppercase tracking-widest text-[9px] opacity-70`}>
                  {isSelected ? "Sélectionné" : "Filtrer"}
                </span>
              </div>
            </div>
          );
        })}

        {/* COMMUNITY RENDER (NO UEMOA AS REQUIRED) */}
        {activeTab === 'COMMUNITY' && communityCategories.map((cat) => {
          const isSelected = selectedDomain === cat.domain;
          return (
            <div
              key={cat.domain}
              onClick={() => setSelectedDomain(isSelected ? null : cat.domain)}
              className={`p-6 rounded-3xl border cursor-pointer transition-all duration-300 flex flex-col justify-between group ${cat.theme.bg} ${isSelected ? `ring-4 ring-offset-2 ${cat.theme.ring} ${cat.theme.border} scale-[1.02] shadow-lg` : `${cat.theme.border} hover:shadow-lg hover:-translate-y-1`}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-2xl bg-white shadow-sm border border-white/40 ring-1 ring-primary-750/5`}>
                    {cat.icon}
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <span className={`text-sm font-extrabold uppercase tracking-wide ${cat.theme.text}`}>
                    {cat.name}
                  </span>
                  <p className="text-xs text-zinc-600 leading-relaxed font-medium">{cat.desc}</p>
                </div>
              </div>

              <div className={`pt-5 flex items-center justify-between text-[11px] font-bold border-t border-primary-750/10 mt-5`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectDomainAndRedirect(cat.domain);
                  }}
                  className={`${cat.theme.btn} flex items-center gap-1.5 focus:outline-none transition-colors group-hover:underline decoration-2 underline-offset-4`}
                >
                  Directement dans le moteur <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <span className={`${cat.theme.text} uppercase tracking-widest text-[9px] opacity-70`}>
                  {isSelected ? "Sélectionné" : "Filtrer"}
                </span>
              </div>
            </div>
          );
        })}

        {/* INTERNATIONAL RENDER */}
        {activeTab === 'INTERNATIONAL' && internationalCategories.map((cat) => {
          const isSelected = selectedDomain === cat.domain;
          return (
            <div
              key={cat.domain}
              onClick={() => setSelectedDomain(isSelected ? null : cat.domain)}
              className={`p-6 rounded-3xl border cursor-pointer transition-all duration-300 flex flex-col justify-between group ${cat.theme.bg} ${isSelected ? `ring-4 ring-offset-2 ${cat.theme.ring} ${cat.theme.border} scale-[1.02] shadow-lg` : `${cat.theme.border} hover:shadow-lg hover:-translate-y-1`}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div className={`p-3 rounded-2xl bg-white shadow-sm border border-white/40 ring-1 ring-primary-750/5`}>
                    {cat.icon}
                  </div>
                </div>
                
                <div className="space-y-1.5">
                  <span className={`text-sm font-extrabold uppercase tracking-wide ${cat.theme.text}`}>
                    {cat.name}
                  </span>
                  <p className="text-xs text-zinc-600 leading-relaxed font-medium">{cat.desc}</p>
                </div>
              </div>

              <div className={`pt-5 flex items-center justify-between text-[11px] font-bold border-t border-primary-750/10 mt-5`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    selectDomainAndRedirect(cat.domain);
                  }}
                  className={`${cat.theme.btn} flex items-center gap-1.5 focus:outline-none transition-colors group-hover:underline decoration-2 underline-offset-4`}
                >
                  Directement dans le moteur <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <span className={`${cat.theme.text} uppercase tracking-widest text-[9px] opacity-70`}>
                  {isSelected ? "Sélectionné" : "Filtrer"}
                </span>
              </div>
            </div>
          );
        })}

      </div>

      {/* SUB-LIST CONTENT LISTING */}
      <div className="w-full h-px bg-gradient-to-r from-transparent via-primary-750/20 to-transparent my-8" />
      <div className="space-y-4">
        <h3 className="font-display text-lg font-bold text-zinc-900 flex items-center gap-2">
          {activeTab === 'NATIONAL' ? <Landmark className="w-4 h-4 text-slate-700" /> : activeTab === 'COMMUNITY' ? <Globe className="w-4 h-4 text-slate-700" /> : <Compass className="w-4 h-4 text-slate-700" />}
          Documents de la catégorie {getSelectedLabel() ? `"${getSelectedLabel()}"` : ""} ({filteredDocs.length})
        </h3>

        {filteredDocs.length === 0 ? (
          <div className="p-10 border border-dashed border-zinc-200 rounded-2xl text-center text-xs text-zinc-400 font-normal bg-zinc-50/30">
            Sélectionnez une catégorie ou cliquez ci-dessus pour charger les actes juridiques.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredDocs.map((doc) => (
              <div
                key={doc.id}
                className="bg-white p-6 rounded-2xl border border-zinc-200/80 hover:border-primary-750/20 hover:shadow-md transition-all flex flex-col justify-between gap-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-[10px] font-mono font-bold bg-primary-50 text-primary-750 px-2.5 py-0.5 rounded-full border border-primary-100/30">
                      Réf: {doc.referenceNumber}
                    </span>
                    <StatusBadge status={doc.legalStatus} size="sm" />
                  </div>
                  <h4 className="font-semibold text-xs text-zinc-800 line-clamp-2 leading-relaxed">
                    {doc.officialTitle}
                  </h4>
                </div>

                <div className="flex items-center justify-between text-[11px] pt-3 border-t border-primary-750/10">
                  <span className="text-zinc-500 font-normal">Adoption : <strong className="text-zinc-700">{doc.effectiveYear}</strong></span>
                  <button
                    onClick={() => {
                      if (isAuthenticated) {
                        setActiveDocumentId(doc.id);
                        navigate(`/document/${doc.id}`, { state: { document: doc } });
                      } else {
                        setSearchQueryState?.(doc.officialTitle);
                        setSearchFiltersState({});
                        setView('search');
                      }
                    }}
                    className="text-primary-750 font-bold hover:text-primary-950 flex items-center gap-0.5 transition-colors cursor-pointer"
                  >
                    {isAuthenticated ? "Consulter →" : "Rechercher l'acte →"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
};
